import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Formik } from "formik";
import { AuthContext } from "../../provider/AuthProvider";
import auth from "../../services/auth";

import { Center } from "../../components/StyledComponents";
import Button from "../../components/Button";
import Form from "../../components/Form";
import Input from "../../components/Form/Input";
import Submit from "../../components/Form/Submit";
import ErrorMessage from "../../components/Form/ErrorMessage";
import { MfaConfig } from "../../components/MfaModals";

import {
  schema as signInSchema,
  initialValues as signInInitialValues,
} from "../../constants/form/auth/sign-in";

import {
  schema as recoveryCodeSchema,
  initialValues as recoveryCodeInitialValues,
} from "../../constants/form/auth/recovery-code";

import { initialValues as passwordRecoveryInitialValues } from "../../constants/form/auth/password-recovery";

import {
  recoverPassword,
  resetPassword,
  signIn,
} from "../../store/actions/auth";
import { RESET_PASSWORD_CLEAR_ERROR } from "../../store/types/auth";
import { AUTH_CODES } from "../../constants/authCodes";

// Helper for tests: build login payload ensuring browser_hash is always signaled
export const buildLoginPayload = (values: any, browserHash: string) => ({
  ...values,
  browser_hash: browserHash || "",
});

interface CustomizedState {
  siginStates: {
    loading: boolean;
    success: boolean;
    error: any;
  };
  from: {
    pathname: string;
    search: string;
    hash: string;
  };
}

const DEFAULT_POST_LOGIN_ROUTE = "/dashboard/profile";
const DEFAULT_PASSWORD_EXPIRED_MESSAGE =
  "Tu contraseña ha expirado. Restablécela para continuar.";

interface StoredMfaCredentials {
  email?: string;
}

const normalizeErrorText = (text: string = "") =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const hasPasswordExpiredMessage = (message: string = "") => {
  const normalizedMessage = normalizeErrorText(message);

  if (!normalizedMessage) {
    return false;
  }

  return (
    normalizedMessage.includes("password_expired") ||
    normalizedMessage.includes("password expired") ||
    normalizedMessage.includes("contrasena ha expirado") ||
    normalizedMessage.includes("contrasena expirada") ||
    normalizedMessage.includes("debe cambiarla") ||
    normalizedMessage.includes("password_change_required")
  );
};

const hasPasswordExpiredFlags = (stateData: any) => {
  if (!stateData) {
    return false;
  }

  const payload = stateData.payload || {};

  return Boolean(
    stateData.isPasswordExpired ||
    stateData.passwordExpired ||
    stateData.passwordChangeRequired ||
    stateData.requiresPasswordChange ||
    stateData.mustChangePassword ||
    stateData.isFirstLogin ||
    payload.isPasswordExpired ||
    payload.passwordExpired ||
    payload.password_expired ||
    payload.passwordChangeRequired ||
    payload.password_change_required ||
    payload.requiresPasswordChange ||
    payload.mustChangePassword ||
    payload.isFirstLogin
  );
};

const isPasswordExpiredResponse = (stateData: any) => {
  if (!stateData) {
    return false;
  }

  const apiCode = stateData.code || stateData.payload?.code;
  const message = stateData.message || stateData.payload?.message || "";

  return (
    apiCode === AUTH_CODES.PASSWORD_EXPIRED ||
    hasPasswordExpiredMessage(message) ||
    hasPasswordExpiredFlags(stateData)
  );
};

const buildRateLimitMessage = (retryAfter?: number) => {
  if (!retryAfter || Number.isNaN(Number(retryAfter))) {
    return "Demasiados intentos desde este origen. Esperá unos minutos e intentá nuevamente.";
  }

  const retryAfterSeconds = Math.max(0, Math.ceil(Number(retryAfter)));
  const minutes = Math.ceil(retryAfterSeconds / 60);

  if (minutes <= 1) {
    return "Demasiados intentos desde este origen. Reintentá en 1 minuto.";
  }

  return `Demasiados intentos desde este origen. Reintentá en ${minutes} minutos.`;
};

const mfaTokenSchema = Yup.object().shape({
  token: Yup.string()
    .required("Este campo es requerido")
    .length(6, "El código debe tener 6 dígitos")
    .matches(/^[0-9]+$/, "Solo se permiten números"),
});

const mfaBackupSchema = Yup.object().shape({
  backupCode: Yup.string()
    .required("Este campo es requerido")
    .min(1, "Debe ingresar el código de respaldo"),
});

const Page = ({
  userStates,
  authStates,
  recoverPasswordStates,
  recoverPasswordInformation,
  resetPasswordStates,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);
  const fromState = (location.state as CustomizedState)?.from;
  const fromPathname = fromState?.pathname || "";
  const from =
    fromPathname &&
      fromPathname !== "/" &&
      !fromPathname.startsWith("/sign-in") &&
      !fromPathname.startsWith("/sign-up")
      ? `${fromPathname}${fromState?.search || ""}${fromState?.hash || ""}`
      : DEFAULT_POST_LOGIN_ROUTE;

  useEffect(() => {
    const sessionInvalidated = localStorage.getItem("session_invalidated");
    if (sessionInvalidated === "true") {
      toast.error(
        "Tu sesión ha caducado o ha sido cerrada por un nuevo inicio de sesión.",
        {
          toastId: "session-invalidated",
        }
      );
      localStorage.removeItem("session_invalidated");
    }
  }, []);

  const dispatch = useDispatch();

  const { loading: userLoading } = userStates;
  const { error: authError, loading: authLoading, data: authData } = authStates;

  const searchParams = new URLSearchParams(location.search);
  const emailFromUrl = searchParams.get("email") || "";
  const codeFromUrl = searchParams.get("code") || "";
  const verificationFromUrl = searchParams.get("verification") || "";
  const isRecoveryUrl = !!(emailFromUrl && codeFromUrl && verificationFromUrl);

  const [showLogin, setShowLogin] = useState(!isRecoveryUrl);
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(isRecoveryUrl);
  const [accountToRecover, setAccountToRecover] = useState(emailFromUrl);
  const [passwordResetFormActions, setPasswordResetFormActions] = useState<any>(null);

  const [showMfaChallenge, setShowMfaChallenge] = useState(false);
  const [showMfaRecovery, setShowMfaRecovery] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState<any>(null);
  const [openMfaConfig, setOpenMfaConfig] = useState(false);
  const [userInfoForMfa, setUserInfoForMfa] = useState<any>(null);

  const [showConflict, setShowConflict] = useState(false);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const [conflictLoggingOut, setConflictLoggingOut] = useState(false);
  const [conflictPayload, setConflictPayload] = useState<any>(null);

  const [lockoutMessage, setLockoutMessage] = useState("");
  const [passwordExpiredMessage, setPasswordExpiredMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordRecentlyUsed, setPasswordRecentlyUsed] = useState(false);

  const loginEmailRef = useRef<HTMLInputElement>(null);
  const mfaTokenRef = useRef<HTMLInputElement>(null);
  const mfaBackupRef = useRef<HTMLInputElement>(null);
  const recoveryEmailRef = useRef<HTMLInputElement>(null);
  const changePasswordCodeRef = useRef<HTMLInputElement>(null);

  const shownToastsRef = useRef<Set<string>>(new Set());
  const lastProcessedAuthDataContentRef = useRef<string>("");
  const lastProcessedAuthErrorContentRef = useRef<string>("");
  const loginCredentialsRef = useRef(loginCredentials);

  useEffect(() => {
    loginCredentialsRef.current = loginCredentials;
  }, [loginCredentials]);

  const getSafeBrowserHash = () => {
    const raw = localStorage.getItem("browserHash") || "";
    try {
      if (raw.startsWith('"') && raw.endsWith('"')) {
        return JSON.parse(raw);
      }
    } catch (e) {}
    return raw;
  };
  const browserHash = getSafeBrowserHash();

  const getStoredMfaCredentials = (): StoredMfaCredentials => {
    const stored = sessionStorage.getItem("mfaLoginCredentials");
    if (!stored) {
      return {};
    }

    try {
      return JSON.parse(stored) as StoredMfaCredentials;
    } catch {
      return {};
    }
  };

  const openPasswordRecoveryFlow = useCallback((message: string) => {
    const resolvedMessage = message || DEFAULT_PASSWORD_EXPIRED_MESSAGE;
    const toastKey = `PASSWORD_EXPIRED::${resolvedMessage}`;

    if (!shownToastsRef.current.has(toastKey)) {
      shownToastsRef.current.add(toastKey);
      toast.warning(resolvedMessage);
    }

    const storedCredentials = getStoredMfaCredentials();
    const email =
      loginCredentialsRef.current?.email ||
      storedCredentials.email ||
      accountToRecover ||
      "";

    setAccountToRecover(email);
    setPasswordExpiredMessage(resolvedMessage);
    setShowMfaChallenge(false);
    setShowMfaRecovery(false);
    setShowConflict(false);
    setShowPasswordChange(false);
    setShowLogin(false);
    setShowPasswordRecovery(true);
  }, [accountToRecover]);

  // (moved to module scope for testability)

  const appSignIn = ({ values }) => {
    setLoginCredentials(values);
    sessionStorage.setItem(
      "mfaLoginCredentials",
      JSON.stringify({ email: values.email })
    );
    setErrorMessage("");
    setLockoutMessage("");
    setPasswordExpiredMessage("");
    setShowConflict(false);
    shownToastsRef.current.clear();
    toast.dismiss();
    lastProcessedAuthDataContentRef.current = "";
    lastProcessedAuthErrorContentRef.current = "";

    const loginData = buildLoginPayload(values, browserHash);

    setConflictPayload(loginData);
    dispatch(signIn(loginData));
    setIsSubmittingLocal(true);
  };

  const handleMfaSubmit = ({ values }) => {
    setErrorMessage("");
    setLockoutMessage("");
    shownToastsRef.current.clear();
    toast.dismiss();
    setIsSubmittingLocal(true);

    let credentials = loginCredentials;
    const storedCredentials = getStoredMfaCredentials();
    if ((!credentials || !credentials.email) && storedCredentials.email) {
      credentials = {
        ...credentials,
        email: storedCredentials.email,
      };
    }

    if (!credentials || !credentials.email || !credentials.password) {
      setErrorMessage("Error: credenciales no disponibles. Inicia sesión nuevamente.");
      setShowMfaChallenge(false);
      setShowLogin(true);
      setIsSubmittingLocal(false); // Reset if credentials are not available
      return;
    }

    const loginData = {
      email: credentials.email,
      password: credentials.password,
      twofa: showMfaRecovery ? undefined : values.token,
      backupCode: showMfaRecovery ? values.backupCode : undefined,
      browser_hash: browserHash || "",
    };

    if (rememberDevice && !showMfaRecovery) {
      localStorage.setItem("rememberDevice", "true");
    }

    setConflictPayload(loginData);
    dispatch(signIn(loginData));
    setIsSubmittingLocal(true);
  };

  const sendRecoveryCode = ({ values }) => {
    setErrorMessage("");
    shownToastsRef.current.clear();
    toast.dismiss();
    dispatch(recoverPassword(values));
    setIsSubmittingLocal(true);
    setAccountToRecover(values.email);
  };

  const changePassword = ({ values, actions }) => {
    setPasswordResetFormActions(actions);
    shownToastsRef.current.clear();
    toast.dismiss();
    dispatch(
      resetPassword({
        ...values,
        verification:
          verificationFromUrl || recoverPasswordInformation?.result?.verification,
      })
    );
    setIsSubmittingLocal(true);
  };

  const handleConflictCancel = () => {
    setShowConflict(false);
    setShowLogin(true);
  };

  const handleConflictContinue = async () => {
    setConflictLoggingOut(true);
    try {
      // Pass credentials to allow logout without a valid token (conflict resolution)
      await auth.logoutSession({
        email: loginCredentialsRef.current?.email,
        password: loginCredentialsRef.current?.password,
      });
      setShowConflict(false);
      if (conflictPayload) {
        dispatch(signIn(conflictPayload));
      } else {
        setShowLogin(true);
      }
    } finally {
      setConflictLoggingOut(false);
    }
  };

  useEffect(() => {
    if (!authData) {
      return;
    }

    const responseContent = JSON.stringify({
      code: authData.httpCode || authData.status || authData.code,
      message: authData.message,
      timestamp: authData.timestamp,
    });

    if (
      lastProcessedAuthDataContentRef.current === responseContent ||
      (!isSubmittingLocal && !authLoading)
    ) {
      return;
    }

    const code = Number(authData.httpCode || authData.status);
    const apiCode = authData.code;
    const message = authData.message || "";

    if (isPasswordExpiredResponse(authData)) {
      // With the latest back-end changes (ADE-4809), we allow login even with expired passwords.
      // The AuthProvider and RequireAuth guard will handle the redirection to the profile page.
      // We no longer block the login flow here.
    }

    if (
      code === 202 ||
      code === 203 ||
      apiCode === AUTH_CODES.MFA_SETUP ||
      message.includes("respaldo")
    ) {
      toast.info("Configure nuevamente la autenticación de dos factores");
      setShowMfaChallenge(false);
      setShowMfaRecovery(false);
      setShowLogin(true);
      setOpenMfaConfig(true);
      setUserInfoForMfa(loginCredentialsRef.current);
    }

    if (code === 409 || apiCode === AUTH_CODES.CONFLICT) {
      setShowLogin(false);
      setShowMfaChallenge(false);
      setShowConflict(true);
    }

    setIsSubmittingLocal(false);
    lastProcessedAuthDataContentRef.current = responseContent;
  }, [authData, openPasswordRecoveryFlow, isSubmittingLocal, authLoading]);

  useEffect(() => {
    if (!authError) {
      return;
    }

    const errorContent = JSON.stringify({
      code: authError.httpCode || authError.status || authError.code,
      message: authError.message,
      timestamp: authError.timestamp,
    });

    const code = Number(authError.httpCode || authError.status);
    const apiCode = authError.code;
    const isMfaChallengeResponse =
      code === 428 || apiCode === AUTH_CODES.MFA_CHALLENGE;

    if (
      lastProcessedAuthErrorContentRef.current === errorContent ||
      ((!isSubmittingLocal && !authLoading) && !isMfaChallengeResponse)
    ) {
      return;
    }

    const message = authError.message || "";
    const passwordExpired = isPasswordExpiredResponse(authError);

    if (passwordExpired) {
      // With the latest back-end changes (ADE-4809), password expiration no longer returns a 401.
      // If it still happens for some legacy reason, we allow the AuthProvider to handle it.
    }

    if (code === 409 || apiCode === AUTH_CODES.CONFLICT) {
      if (!conflictPayload && loginCredentialsRef.current) {
        setConflictPayload(buildLoginPayload(loginCredentialsRef.current, browserHash));
      }
      setShowLogin(false);
      setShowMfaChallenge(false);
      setShowConflict(true);
      setErrorMessage(message || "Existe una sesión activa para tu cuenta.");
    } else if (code === 428 || apiCode === AUTH_CODES.MFA_CHALLENGE) {
      setLockoutMessage("");
      toast.dismiss();
      if (showMfaChallenge) {
        const errorMsg =
          message ||
          (showMfaRecovery
            ? "Código de respaldo incorrecto"
            : "Código de verificación incorrecto");
        setErrorMessage(errorMsg);
      } else {
        setShowLogin(false);
        setShowMfaChallenge(true);
        setShowMfaRecovery(false);
        setErrorMessage(
          message || "Ingrese código de autenticación, tienes 3 intentos"
        );
      }
    } else if (code === 203 || apiCode === AUTH_CODES.MFA_SETUP) {
      const warningMessage =
        message || "Debes configurar la autenticación de dos factores para ingresar.";
      if (!shownToastsRef.current.has(warningMessage)) {
        shownToastsRef.current.add(warningMessage);
        toast.warning(warningMessage);
      }
      setOpenMfaConfig(true);
      setUserInfoForMfa(loginCredentialsRef.current);
    } else if (code === 429 || apiCode === AUTH_CODES.RATE_LIMITED) {
      const retryAfter = Number(authError?.payload?.retryAfter);
      const warningMessage = buildRateLimitMessage(retryAfter);
      setErrorMessage(warningMessage);

      if (!shownToastsRef.current.has(warningMessage)) {
        shownToastsRef.current.add(warningMessage);
        toast.error(warningMessage);
      }

      setShowMfaChallenge(false);
      setShowLogin(true);
    } else if (code === 423) {
      const errorMsg =
        "Usuario o Contraseña incorrecta. Si tu cuenta fue bloqueada, te enviamos un correo con instrucciones.";
      setLockoutMessage("");
      setErrorMessage(errorMsg);

      if (!shownToastsRef.current.has(errorMsg)) {
        shownToastsRef.current.add(errorMsg);
        toast.error(errorMsg);
      }

      setShowMfaChallenge(false);
      setShowLogin(true);
    } else if (code === 403) {
      const warningMessage =
        message || "Habilite su autenticación 2FA para ingresar";
      if (!shownToastsRef.current.has(warningMessage)) {
        shownToastsRef.current.add(warningMessage);
        toast.warning(warningMessage);
      }
      setOpenMfaConfig(true);
      setUserInfoForMfa(loginCredentialsRef.current);
    } else if (code === 202) {
      toast.info("Configure nuevamente la autenticación de dos factores");
      setShowMfaChallenge(false);
      setShowMfaRecovery(false);
      setShowLogin(true);
      setOpenMfaConfig(true);
      setUserInfoForMfa(loginCredentialsRef.current);
    } else if (code === 401) {
      if (showMfaChallenge) {
        const errorMsg = showMfaRecovery
          ? "Código de respaldo incorrecto"
          : "Código de verificación incorrecto";
        setErrorMessage(errorMsg);
        if (!shownToastsRef.current.has(errorMsg)) {
          shownToastsRef.current.add(errorMsg);
          toast.error(errorMsg);
        }
      } else {
        const errorMsg = message || "Usuario o Contraseña incorrecta";
        setErrorMessage(errorMsg);
        if (!shownToastsRef.current.has(errorMsg)) {
          shownToastsRef.current.add(errorMsg);
          toast.error(errorMsg);
        }
      }
    } else {
      const errorMsg = message || "Usuario o Contraseña incorrecta";
      setErrorMessage(errorMsg);
      if (showMfaChallenge) {
        const mfaErrorMsg = showMfaRecovery
          ? "Código de respaldo incorrecto"
          : "Código de verificación incorrecto";
        if (!shownToastsRef.current.has(mfaErrorMsg)) {
          shownToastsRef.current.add(mfaErrorMsg);
          toast.error(mfaErrorMsg);
        }
      } else {
        setShowLogin(true);
        setShowPasswordRecovery(false);
        setShowMfaChallenge(false);
      }
    }

    setIsSubmittingLocal(false);
    lastProcessedAuthErrorContentRef.current = errorContent;
  }, [
    authError,
    showMfaChallenge,
    showMfaRecovery,
    browserHash,
    conflictPayload,
    openPasswordRecoveryFlow,
    isSubmittingLocal,
    authLoading,
  ]);

  useEffect(() => {
    if (recoverPasswordStates.success && isSubmittingLocal) {
      toast.success("Si tu email está registrado, te enviamos un código.");
      setIsSubmittingLocal(false);
      setShowLogin(true);
      setShowPasswordRecovery(false);
      setShowPasswordChange(false);
      setErrorMessage("");
    }
  }, [recoverPasswordStates.success, isSubmittingLocal]);

  useEffect(() => {
    if (recoverPasswordStates.error && isSubmittingLocal) {
      toast.success("Si tu email está registrado, te enviamos un código.");
      setIsSubmittingLocal(false);
      setShowLogin(true);
      setShowPasswordRecovery(false);
      setShowPasswordChange(false);
      setErrorMessage("");
    }
  }, [recoverPasswordStates.error, isSubmittingLocal]);

  useEffect(() => {
    if (showPasswordChange && resetPasswordStates.success && isSubmittingLocal) {
      setShowLogin(true);
      setShowPasswordRecovery(false);
      setShowPasswordChange(false);
      setIsSubmittingLocal(false);
      setPasswordExpiredMessage("");
      setErrorMessage("");
      setPasswordRecentlyUsed(false);
      toast.success("Contraseña cambiada exitosamente");
      dispatch({ type: RESET_PASSWORD_CLEAR_ERROR });
    }
  }, [
    showPasswordChange,
    resetPasswordStates.success,
    isSubmittingLocal,
    dispatch,
  ]);

  useEffect(() => {
    if (showPasswordChange && resetPasswordStates.error && isSubmittingLocal) {
      const error = resetPasswordStates.error as any;
      const message = error.message || "";
      const httpCode = error.httpCode || error.status;

      if (message.includes("PASSWORD_RECENTLY_USED")) {
        setPasswordRecentlyUsed(true);
        setErrorMessage(
          "No puedes usar una contraseña que ya has utilizado recientemente. Elige una contraseña diferente."
        );
        const errorMsg = "Esta contraseña ya fue utilizada recientemente";
        if (!shownToastsRef.current.has(errorMsg)) {
          shownToastsRef.current.add(errorMsg);
          toast.error(errorMsg);
        }
      } else if (httpCode === 404) {
        const errorMsg = message || "Código de recuperación inválido";
        setErrorMessage(message || "El código ingresado es inválido o ya fue utilizado");
        if (!shownToastsRef.current.has(errorMsg)) {
          shownToastsRef.current.add(errorMsg);
          toast.error(errorMsg);
        }
      } else {
        const errorMsg = message || "Error al cambiar la contraseña";
        setErrorMessage(errorMsg);
        if (!shownToastsRef.current.has(errorMsg)) {
          shownToastsRef.current.add(errorMsg);
          toast.error(errorMsg);
        }
      }

      if (passwordResetFormActions) {
        passwordResetFormActions.setSubmitting(false);
      }

      setIsSubmittingLocal(false);
      dispatch({ type: RESET_PASSWORD_CLEAR_ERROR });
    }
  }, [
    showPasswordChange,
    resetPasswordStates.error,
    dispatch,
    passwordResetFormActions,
    isSubmittingLocal,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      if (authData?.browserHash || authData?.browser_hash) {
        localStorage.setItem("browserHash", authData.browserHash || authData.browser_hash);
      }
      sessionStorage.removeItem("mfaLoginCredentials");
      setLoginCredentials(null);
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from, authData]);

  useEffect(() => {
    if (showLogin && !showMfaChallenge && !openMfaConfig && loginEmailRef.current) {
      setTimeout(() => loginEmailRef.current?.focus(), 100);
    }
  }, [showLogin, showMfaChallenge, openMfaConfig]);

  useEffect(() => {
    if (showMfaChallenge && !showMfaRecovery && mfaTokenRef.current) {
      setTimeout(() => mfaTokenRef.current?.focus(), 100);
    }
  }, [showMfaChallenge, showMfaRecovery]);

  useEffect(() => {
    if (showMfaRecovery && mfaBackupRef.current) {
      setTimeout(() => mfaBackupRef.current?.focus(), 100);
    }
  }, [showMfaRecovery]);

  useEffect(() => {
    if (showPasswordRecovery && !showPasswordChange && recoveryEmailRef.current) {
      setTimeout(() => recoveryEmailRef.current?.focus(), 100);
    }
  }, [showPasswordRecovery, showPasswordChange]);

  useEffect(() => {
    if (showPasswordChange && changePasswordCodeRef.current) {
      setTimeout(() => changePasswordCodeRef.current?.focus(), 100);
    }
  }, [showPasswordChange]);

  return (
    <>
      {openMfaConfig && (
        <MfaConfig
          userInfo={userInfoForMfa}
          setOpenConfigModals={setOpenMfaConfig}
          onMfaSetupComplete={() => {
            const storedBrowserHash = getSafeBrowserHash();
            if (storedBrowserHash && userInfoForMfa) {
              dispatch(
                signIn({
                  email: userInfoForMfa.email,
                  password: userInfoForMfa.password,
                  browser_hash: storedBrowserHash,
                })
              );
            }
          }}
        />
      )}

      {showLogin && !showMfaChallenge && !showConflict && (
        <>
          <div>
            <h3>Bienvenido</h3>
            <p>Ingresa tu email y contraseña</p>
          </div>

          <Form
            id="login-form"
            rules={signInSchema}
            values={signInInitialValues}
            onSubmit={appSignIn}
            validateOnBlur={false}
            validateOnChange={false}
          >
            <Input
              type="email"
              name="email"
              placeholder="Ingrese su e-mail"
              options={{ label: "E-mail", marginBottom: 24 }}
              forwardRef={loginEmailRef}
            />

            <Input
              type="password"
              name="password"
              placeholder="Ingrese su contraseña"
              options={{ label: "Contraseña", marginBottom: 24 }}
            />

            <Center style={{ marginBottom: "24px" }}>
              <ErrorMessage visible={!!lockoutMessage}>{lockoutMessage}</ErrorMessage>
              <ErrorMessage visible={!!authError && !lockoutMessage && !!errorMessage}>
                {errorMessage}
              </ErrorMessage>
            </Center>

            <Center horizontal="center" vertical="center">
              <Submit form="login-form" isSubmmiting={authLoading || userLoading}>
                Iniciar sesión
              </Submit>
              <Button
                type="button"
                options={{
                  skin: "primary",
                  size: "lg",
                  type: "outline",
                }}
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  setShowLogin(false);
                  setShowPasswordRecovery(true);
                  setShowPasswordChange(false);
                  setLockoutMessage("");
                  setErrorMessage("");
                }}
              >
                Olvidé mi contraseña
              </Button>
            </Center>
          </Form>
        </>
      )}

      {showMfaChallenge && (
        <>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            {showMfaRecovery ? (
              <>
                <h3>Recuperar cuenta</h3>
                <p>
                  Introduce el código de respaldo que recibiste al configurar la
                  autenticación de dos factores
                </p>
              </>
            ) : (
              <>
                <h3>Autenticación de dos factores</h3>
                <p>Ingresa el código de tu aplicación de autenticación</p>
              </>
            )}
          </div>

          <Formik
            initialValues={showMfaRecovery ? { backupCode: "" } : { token: "" }}
            validationSchema={showMfaRecovery ? mfaBackupSchema : mfaTokenSchema}
            onSubmit={(values) => {
              handleMfaSubmit({ values });
            }}
            enableReinitialize
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setErrors,
              setTouched,
            }) => (
              <form id="mfa-form" onSubmit={handleSubmit}>
                {showMfaRecovery ? (
                  <Input
                    type="text"
                    name="backupCode"
                    placeholder="Ingresa tu código de respaldo"
                    options={{ label: "Código de respaldo", marginBottom: 24 }}
                    value={values.backupCode}
                    error={errors.backupCode}
                    touched={touched.backupCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    forwardRef={mfaBackupRef}
                  />
                ) : (
                  <>
                    <Input
                      type="text"
                      name="token"
                      placeholder="000000"
                      options={{ label: "Código de autenticación", marginBottom: 24 }}
                      value={values.token}
                      error={errors.token}
                      touched={touched.token}
                      onChange={(event) => {
                        const value = event.target.value.replace(/\D/g, "").slice(0, 6);
                        handleChange({ target: { name: "token", value } });
                      }}
                      onBlur={handleBlur}
                      forwardRef={mfaTokenRef}
                    />

                    <div style={{ marginBottom: "24px" }}>
                      <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={rememberDevice}
                          onChange={(event) => setRememberDevice(event.target.checked)}
                          style={{ marginRight: "8px" }}
                        />
                        <span style={{ fontSize: "14px" }}>
                          Recordar este dispositivo por 30 días
                        </span>
                      </label>
                    </div>
                  </>
                )}

                <Center style={{ marginBottom: "16px" }}>
                  <ErrorMessage visible={!!errorMessage}>
                    {errorMessage}
                  </ErrorMessage>
                </Center>

                <Center horizontal="center" vertical="center">
                  <Submit form="mfa-form" isSubmmiting={authLoading}>
                    {showMfaRecovery ? "Recuperar cuenta" : "Verificar"}
                  </Submit>
                  <Button
                    type="button"
                    options={{
                      skin: "secondary",
                      size: "lg",
                      type: "outline",
                    }}
                    style={{ marginLeft: "10px" }}
                    onClick={() => {
                      setShowMfaChallenge(false);
                      setShowLogin(true);
                      setShowMfaRecovery(false);
                    }}
                  >
                    Cancelar
                  </Button>
                </Center>

                {!showMfaRecovery && (
                  <Center style={{ marginTop: "16px" }}>
                    <Button
                      type="button"
                      options={{
                        skin: "primary",
                        size: "sm",
                        type: "link",
                      }}
                      onClick={() => {
                        setShowMfaRecovery(true);
                        setErrors({});
                        setTouched({});
                        setErrorMessage("");
                      }}
                    >
                      ¿Perdiste tu teléfono? Usa un código de respaldo
                    </Button>
                  </Center>
                )}

                {showMfaRecovery && (
                  <Center style={{ marginTop: "16px" }}>
                    <Button
                      type="button"
                      options={{
                        skin: "primary",
                        size: "sm",
                        type: "link",
                      }}
                      onClick={() => {
                        setShowMfaRecovery(false);
                        setErrors({});
                        setTouched({});
                        setErrorMessage("");
                      }}
                    >
                      Volver a autenticación normal
                    </Button>
                  </Center>
                )}
              </form>
            )}
          </Formik>
        </>
      )}

      {showConflict && (
        <>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <h3>Sesión activa detectada</h3>
            <p>Ya existe una sesión activa para tu cuenta.</p>
            <p>¿Deseas cerrar la otra sesión e iniciar una nueva?</p>
          </div>

          <Center style={{ marginBottom: "16px" }}>
            <ErrorMessage visible={!!errorMessage}>{errorMessage}</ErrorMessage>
          </Center>

          <Center horizontal="center" vertical="center">
            <Button
              type="button"
              options={{ skin: "primary", size: "lg", type: "filled" }}
              style={{ marginRight: "10px" }}
              onClick={handleConflictContinue}
            >
              {conflictLoggingOut ? "Cerrando sesión..." : "Cerrar otra sesión"}
            </Button>
            <Button
              type="button"
              options={{ skin: "secondary", size: "lg", type: "outline" }}
              onClick={handleConflictCancel}
            >
              Cancelar
            </Button>
          </Center>
        </>
      )}

      {showPasswordRecovery && (
        <>
          <div>
            <h3>Recupera tu contraseña</h3>
            <p>{passwordExpiredMessage ? passwordExpiredMessage : "Ingresa tu e-mail"}</p>
          </div>

          {errorMessage && (
            <Center style={{ marginBottom: "24px" }}>
              <ErrorMessage visible>{errorMessage}</ErrorMessage>
            </Center>
          )}

          <Form
            id="recover-code-form"
            rules={recoveryCodeSchema}
            values={{
              ...recoveryCodeInitialValues,
              email: accountToRecover || "",
            }}
            onSubmit={sendRecoveryCode}
            validateOnBlur={false}
            validateOnChange={false}
          >
            <Input
              type="email"
              name="email"
              placeholder="Ingrese su e-mail"
              options={{ label: "E-mail" }}
              forwardRef={recoveryEmailRef}
            />

            <Center horizontal="center" vertical="center">
              <Submit
                form="recover-code-form"
                isSubmmiting={recoverPasswordStates.loading}
              >
                Recuperar contraseña
              </Submit>

              <Button
                type="button"
                options={{
                  skin: "primary",
                  size: "lg",
                  type: "outline",
                }}
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  setShowLogin(true);
                  setShowPasswordRecovery(false);
                  setShowPasswordChange(false);
                  setPasswordExpiredMessage("");
                  setErrorMessage("");
                  setLockoutMessage("");
                }}
              >
                Cancelar
              </Button>
            </Center>
          </Form>
        </>
      )}

      {showPasswordChange && (
        <>
          <div>
            <h3>Recupera tu contraseña</h3>
            <p>
              Ingresa el código de recuperación que enviamos a tu correo
              electrónico.
            </p>
          </div>

          {errorMessage && (
            <Center style={{ marginBottom: "24px" }}>
              <ErrorMessage visible>{errorMessage}</ErrorMessage>
            </Center>
          )}

          <Formik
            initialValues={{
              ...passwordRecoveryInitialValues,
              email: accountToRecover,
              code: codeFromUrl,
              verification: verificationFromUrl,
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().email("Email inválido").required("Este campo es requerido"),
              code: Yup.string().required("Este campo es requerido"),
              password: Yup.string()
                .required("Este campo es requerido")
                .min(8, "Debe tener al menos 8 caracteres")
                .matches(/[A-Z]/, "Debe contener al menos 1 letra mayúscula")
                .matches(/[0-9]/, "Debe contener al menos 1 número")
                .matches(/[^A-Za-z0-9]/, "Debe contener al menos 1 símbolo"),
              "confirm-password": Yup.string()
                .required("Este campo es requerido")
                .oneOf([Yup.ref("password")], "Las contraseñas deben coincidir"),
            })}
            onSubmit={(values, actions) => {
              changePassword({ values, actions });
            }}
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => {
              const password = values.password || "";
              const hasMinLength = password.length >= 8;
              const hasUpperCase = /[A-Z]/.test(password);
              const hasNumber = /[0-9]/.test(password);
              const hasSymbol = /[^A-Za-z0-9]/.test(password);

              return (
                <form onSubmit={handleSubmit}>
                  <input
                    type="hidden"
                    name="verification"
                    value={verificationFromUrl || recoverPasswordInformation?.result?.verification || ""}
                  />

                  <Input
                    type="text"
                    readOnly
                    name="email"
                    options={{ label: "Email" }}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <Input
                    type="text"
                    name="code"
                    placeholder="xxxxxx"
                    options={{ label: "Código de recuperación" }}
                    autoComplete="off"
                    forwardRef={changePasswordCodeRef}
                    value={values.code}
                    error={errors.code}
                    touched={touched.code}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <Input
                    type="password"
                    name="password"
                    placeholder="Ingrese su contraseña"
                    options={{ label: "Nueva contraseña" }}
                    autoComplete="off"
                    value={values.password}
                    error={errors.password}
                    touched={touched.password}
                    onChange={(event) => {
                      handleChange(event);
                      setPasswordRecentlyUsed(false);
                      setErrorMessage("");
                    }}
                    onBlur={handleBlur}
                  />

                  <Input
                    type="password"
                    name="confirm-password"
                    placeholder="Ingrese su contraseña"
                    options={{ label: "Confirmar contraseña" }}
                    autoComplete="off"
                    value={values["confirm-password"]}
                    error={errors["confirm-password"]}
                    touched={touched["confirm-password"]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "16px" }}>
                    <p style={{ marginBottom: "8px" }}>
                      <strong>Requisitos de la contraseña:</strong>
                    </p>
                    <ul style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
                      <li style={{ marginBottom: "4px", display: "flex", alignItems: "center" }}>
                        <span style={{ marginRight: "8px", fontSize: "14px", color: passwordRecentlyUsed ? "#ef4444" : "#666" }}>
                          {passwordRecentlyUsed ? "x" : "o"}
                        </span>
                        <span style={{ color: passwordRecentlyUsed ? "#ef4444" : "#666" }}>
                          No debes usar una contraseña usada recientemente
                        </span>
                      </li>
                      <li style={{ marginBottom: "4px", display: "flex", alignItems: "center" }}>
                        <span style={{ marginRight: "8px", fontSize: "14px", color: hasMinLength ? "#10b981" : "#ef4444" }}>
                          {hasMinLength ? "OK" : "X"}
                        </span>
                        <span style={{ color: hasMinLength ? "#10b981" : "#666" }}>
                          Debe tener al menos 8 caracteres
                        </span>
                      </li>
                      <li style={{ marginBottom: "4px", display: "flex", alignItems: "center" }}>
                        <span
                          style={{
                            marginRight: "8px",
                            fontSize: "14px",
                            color: hasUpperCase && hasSymbol && hasNumber ? "#10b981" : "#ef4444",
                          }}
                        >
                          {hasUpperCase && hasSymbol && hasNumber ? "OK" : "X"}
                        </span>
                        <span style={{ color: hasUpperCase && hasSymbol && hasNumber ? "#10b981" : "#666" }}>
                          Debe contener al menos 1 letra mayúscula, 1 símbolo y 1 número
                        </span>
                      </li>
                    </ul>
                  </div>

                  <Center horizontal="center" vertical="center">
                    <Button
                      type="submit"
                      options={{
                        skin: "primary",
                        size: "lg",
                        type: "filled",
                      }}
                      disabled={isSubmitting || resetPasswordStates.loading}
                    >
                      Cambiar contraseña
                    </Button>

                    <Button
                      type="button"
                      options={{
                        skin: "primary",
                        size: "lg",
                        type: "outline",
                      }}
                      style={{ marginLeft: "10px" }}
                      onClick={() => {
                        setShowLogin(true);
                        setShowPasswordRecovery(false);
                        setShowPasswordChange(false);
                        setPasswordExpiredMessage("");
                        setErrorMessage("");
                        setLockoutMessage("");
                        setPasswordRecentlyUsed(false);
                      }}
                    >
                      Cancelar
                    </Button>
                  </Center>
                </form>
              );
            }}
          </Formik>
        </>
      )}
    </>
  );
};

const state = ({ authStore, userStore }) => {
  const { signin, recoverPassword, resetPassword } = authStore;
  const { states: authStates, data: authData } = signin;
  const { states: recoverPasswordStates, data: recoverPasswordInformation } =
    recoverPassword;
  const { states: resetPasswordStates } = resetPassword;
  const { states: userStates } = userStore.information;
  return {
    authStates: { ...authStates, data: authData },
    recoverPasswordStates,
    recoverPasswordInformation,
    resetPasswordStates,
    userStates,
  };
};

export default connect(state)(Page);
